/**
 * V2-F — the ONE place production selects between the two `FloodSolver`
 * implementations. Every application caller (`Flood3DView.vue`) consumes
 * the result only through `FloodSolver` (plus the one extra read-back
 * method both implementations happen to share, see `ProductionFloodSolver`
 * below) — never `instanceof`, never a concrete class name, never a
 * solver-name string comparison outside this file.
 *
 * Numerical math is untouched: this file constructs, it does not compute.
 */
import type * as THREE from 'three/webgpu'
import type { FloodAnalysisOptions, FloodSolver } from './flood-solver'
import { DonorCellSolverV1 } from './donor-cell-solver-v1'
import { ConservativeTwoPassSolverV2 } from './conservative-two-pass-solver-v2'

export type FloodSolverKind = 'v1' | 'v2'

/**
 * `readDepthBuffer` is NOT part of the frozen `FloodSolver` contract (it is
 * a dev/diagnostic read-back both `DonorCellSolverV1` and
 * `ConservativeTwoPassSolverV2` happen to implement identically) — extending
 * it here, exactly as `FloodPhysicsValidation.vue`'s own `ValidationSolver`
 * already does, lets production diagnostics (`Flood3DView.vue`'s depth
 * probe / donor-overdraw measurement) call it generically without touching
 * the interface itself or naming either concrete class.
 */
export interface ProductionFloodSolver extends FloodSolver {
  readDepthBuffer(renderer: THREE.WebGPURenderer): Promise<Float32Array>
}

export interface CreateFloodSolverOptions {
  gridSize: number
  cellSizeM: number
  bedTexture: THREE.Texture
  initialDepthM?: number
  /** Full per-cell starting depth, overrides `initialDepthM`. Production never sets this; dev/probe callers may. */
  initialDepthGrid?: Float32Array
  /** Same default (12) and meaning in both implementations. Production never overrides it; dev/probe callers may. */
  conductance?: number
  /**
   * Phase 3 analysis outputs, fixed at construction. Only V2 computes them;
   * V1 ignores the option entirely and keeps publishing depth alone, which is
   * what keeps the rollback path unaffected. Production passes
   * {@link PRODUCTION_ANALYSIS_OPTIONS} whenever `config.floodAnalysisEnabled`
   * is true (see `Flood3DView.vue`'s `createSolverAndRenderer`); dev/probe
   * callers may pass any subset directly.
   */
  analysis?: FloodAnalysisOptions
}

/**
 * Resolves the raw config string to a concrete kind, with the fallback
 * split the phase decided on: unset (or empty) means "no override" and
 * takes the current production default; an explicit but UNRECOGNIZED value
 * falls back to `v1` — the conservative rollback side — rather than
 * silently taking whatever the default happens to be, since a typo'd
 * override is more likely a mistake than an intent to accept the default.
 */
export function resolveFloodSolverKind(raw: string | undefined): FloodSolverKind {
  if (raw === 'v1' || raw === 'v2') return raw
  if (!raw) return 'v2'
  console.warn(`flood-solver-factory: unrecognized VITE_APP_FLOOD_SOLVER "${raw}", falling back to v1 (rollback default)`)
  return 'v1'
}

/**
 * Resolves the Phase-4 analysis kill switch. Unset (or empty) means "no
 * override" and takes the production default of ENABLED. `true`/`on`/`1`
 * (case-insensitive, trimmed) enable it explicitly; `false`/`off`/`0` disable
 * it explicitly.
 *
 * The invalid-value fallback is `false` — the OPPOSITE direction from
 * {@link resolveFloodSolverKind}'s fallback to `v1`, deliberately. That
 * function's fallback preserves a FEATURE (the rollback solver still serves
 * water); this one is an operational kill switch for an OPTIONAL, costed
 * feature layered on top of an already-shipping solver. An operator trying to
 * disable analysis on problematic hardware who mistypes the value must land
 * on depth-only V2, not silently keep the expensive optional feature running
 * because a typo was read as "no opinion, use the default."
 */
export function resolveFloodAnalysisEnabled(raw: string | undefined): boolean {
  const value = raw?.trim().toLowerCase()
  if (!value) return true
  if (value === 'true' || value === 'on' || value === '1') return true
  if (value === 'false' || value === 'off' || value === '0') return false
  console.warn(`flood-solver-factory: unrecognized VITE_APP_FLOOD_ANALYSIS "${raw}", falling back to false (analysis disabled)`)
  return false
}

/**
 * Production analysis capability set: all four Phase-3 outputs enabled, wet
 * threshold left at {@link DEFAULT_ANALYSIS_WET_THRESHOLD_M}. Always-on
 * rather than opt-in — capabilities are construction-time immutable, so an
 * opt-in scheme would force a restart to view a historical field partway
 * through a run. Measured cost (+0.065 ms/substep x 24 worst-case) is small
 * against a 16.7 ms frame budget; {@link resolveFloodAnalysisEnabled} remains
 * the escape hatch for hardware where that assumption doesn't hold.
 */
export const PRODUCTION_ANALYSIS_OPTIONS: FloodAnalysisOptions = {
  maxDepth: true,
  arrivalTime: true,
  duration: true,
  flux: true,
}

/**
 * Constructs the selected solver. Logs the active kind ONCE per
 * construction (an AOI load, not a per-frame event) — the only production
 * telemetry this phase adds, for support/debugging visibility.
 *
 * Model identity, for anyone reading logs/support tickets:
 *   v1 — legacy donor-cell solver.
 *   v2 — conservative two-pass donor-cell/diffusive solver.
 * Neither is a full shallow-water/dynamic-wave/Navier-Stokes solver.
 */
export function createFloodSolver(kind: FloodSolverKind, options: CreateFloodSolverOptions): ProductionFloodSolver {
  console.info(`Flood solver: ${kind}`)
  return kind === 'v1' ? new DonorCellSolverV1(options) : new ConservativeTwoPassSolverV2(options)
}
